/**
 * app.js — Boot, router SPA, estado global, módulos habilitados.
 */
window.APP = (() => {
  // Mapa hash (español en href) → clave interna del PAGE_MAP
  const HASH_ALIAS = {
    'cursos':        'courses',
    'recursos':      'resources',
    'biblioteca':    'resources',
    'espacios':      'communities',
    'documentos':    'documents',
    // Revisión 2026-08-27 — `#solicitudes` apuntaba a `requests`, la vista de colaborador
    // del módulo LEGADO de Formatos PDF (documents.py). El portal tenía así dos módulos
    // llamados «Solicitudes» a la vez, con dos series de radicado y dos bandejas: el
    // enlace viejo llevaba a la puerta equivocada y lo que se radicaba ahí no aparecía
    // nunca en «Mis solicitudes». Ahora ambos hashes llevan al módulo vigente, así que
    // los marcadores y enlaces antiguos siguen funcionando y aterrizan donde deben.
    // `requests` sigue en el PAGE_MAP: la administración de plantillas necesita poder
    // abrir lo ya radicado, que es evidencia. Ver la cabecera de documents.py.
    'solicitudes':   'solicitudes',
    'tramites':      'solicitudes',
    'recursos-privados': 'private-records',
    'eventos':       'events',
    'eventos-calendario': 'calendar',
    'encuestas':     'surveys',
    'celebraciones': 'celebrations',
    'directorio':    'directory',
    'notificaciones':'notifications',
    'comunicaciones':'communications',
    // Reorganización de navegación v2 — alias nuevos (los viejos siguen vigentes)
    'novedades':     'feed',
    'publicaciones': 'feed',
    'formacion':     'courses',
    'gestion-conocimiento': 'private-records',
    'centro-notificaciones': 'communications',
    'induccion':     'induction',
    'rutas':         'learning-paths',
    'competencias':  'competencies',
    'comunidades':   'communities',
    'perfil':        'profile',
    'auditoria':     'audit',
    'sistema':       'sistema',
    'informes':      'reports',
    'analitica':     'analytics',
    // identidades (mismo nombre en ambos lados)
    'dashboard':     'dashboard',
    'feed':          'feed',
    'kudos':         'kudos',
    'admin':         'admin',
  };

  const PAGE_MAP = {
    // Migrada a la capa React el 2026-09-07. `page-dashboard.js` se borró en
    // ese mismo cambio, así que **el retroceso de una línea ya no existe para
    // esta pantalla**: no hay versión vanilla a la que volver. Sale del
    // historial de git si hiciera falta.
    //
    // Es el destino de rebote del router: los cuatro `navigate('dashboard')`
    // de los guards de más abajo terminan aquí, igual que un hash desconocido
    // y el arranque sin hash. Si esta entrada falla, el portal se queda sin
    // salida — por eso tiene su propia prueba.
    dashboard:       { react: 'inicio',                      title: 'Inicio',             module: null },
    // Migrada a la capa React el 2026-09-08. `page-feed.js` y `modules/widgets.js`
    // se borraron en ese mismo cambio, así que **el retroceso de una línea ya no
    // existe para esta pantalla**: no hay versión vanilla a la que volver. Sale
    // del historial de git si hiciera falta.
    //
    // Su tarjeta de publicación la usa también Comunidades. Hasta el 2026-09-18
    // eso obligaba a una raíz React incrustada dentro de HTML vanilla
    // (`PortalReact.montarPublicaciones`); al migrar Comunidades esa puerta se
    // cerró y ya no queda ninguna en el portal.
    feed:            { react: 'novedades',                   title: 'Publicaciones',      module: 'feed' },
    courses:         { react: 'cursos',                      title: 'Cursos',             module: 'cursos' },
    'course-detail': { react: 'detalle-curso',               title: 'Detalle del Curso',  module: 'cursos' },
    // `comunidades`, no `feed`: es la clave que exige el backend en las doce rutas
    // de /api/communities (require_module("comunidades")) y la que usa el sidebar
    // en su data-module. El router preguntaba por otra, así que las dos mitades del
    // mismo permiso discrepaban: quitarle `comunidades` a un rol y dejarle `feed`
    // escondía la opción del menú pero dejaba pasar el hash, y la página se pintaba
    // entera con cada llamada devolviendo 403; al revés —`comunidades` sin `feed`—
    // la opción se veía y al pulsarla rebotaba a Inicio. Es el mismo defecto que ya
    // corrigió el guard de permiso de abajo: menú y router tienen que preguntar lo
    // mismo que autoriza el servidor.
    communities:     { react: 'comunidades',                 title: 'Comunidades Internas', module: 'comunidades' },
    'learning-paths':{ react: 'rutas',                       title: 'Rutas de Aprendizaje', module: 'cursos' },
    competencies:    { react: 'competencias',                title: 'Competencias',        module: 'cursos' },
    // Migradas a la capa React el 2026-09-17 (módulo 8). `page-events.js`,
    // `page-calendar.js` y `page-event-gallery.js` se borraron en el mismo
    // cambio: el retroceso es un `git revert`, no una línea. `#eventos/<id>`
    // abre un evento concreto; lo usan la búsqueda global y el Inicio.
    events:          { react: 'eventos',                     title: 'Eventos',            module: 'eventos' },
    calendar:        { react: 'calendario',                  title: 'Calendario Corporativo', module: 'eventos' },
    'event-gallery': { react: 'galeria-evento',              title: 'Galería del Evento', module: 'eventos' },
    kudos:           { react: 'reconocimientos',             title: 'Reconocimientos',    module: 'kudos' },
    // Migrada a la capa React el 2026-09-16. `page-resources.js` se borró en
    // ese mismo cambio: **el retroceso de una línea ya no existe**.
    //
    // Admite sub-ruta: `#biblioteca/<id>` abre ese recurso. Por ahí entra la
    // búsqueda global, que hasta ese día devolvía `"route": "resources"` a
    // secas y dejaba a quien buscaba un manual delante de la biblioteca entera.
    resources:       { react: 'biblioteca',                  title: 'Biblioteca Corporativa', module: 'recursos' },
    requests:        { react: 'mis-documentos',                title: 'Solicitudes',  module: null },
    // Migrada a la capa React el 2026-09-06. `page-solicitudes.js` se borró en
    // ese mismo cambio, así que **el retroceso de una línea ya no existe para
    // esta pantalla**: no hay versión vanilla a la que volver. Sale del
    // historial de git si hiciera falta.
    //
    // Admite sub-ruta: `#tramites/<vista>` abre una pestaña concreta. El mapa de
    // nombres vive en features/solicitudes/Solicitudes.tsx. Por ahí entran los
    // enlaces de Inicio (`#tramites/mis`, `#tramites/aprobaciones`).
    solicitudes:     { react: 'solicitudes',                 title: 'Solicitudes',        module: null },
    documents:       { react: 'documentos',                  title: 'Documentos',          module: null, permiso: ['documentos', 'manage'] },
    'private-records':{ react: 'recursos-y-datos',              title: 'Recursos y Datos', module: null, permiso: ['recursos_privados', 'manage'] },
    surveys:         { react: 'encuestas',                   title: 'Encuestas',          module: 'encuestas' },
    celebrations:    { react: 'celebraciones',               title: 'Celebraciones',      module: 'celebraciones' },
    directory:       { react: 'directorio',                  title: 'Directorio',         module: 'directorio' },
    // Las dos pantallas del Centro de Notificaciones exigen `manage`, no `view`.
    //
    // Hasta el 2026-09-15 pedían solo `module: 'notificaciones'` —que es
    // `view`— mientras los CATORCE endpoints de los dos routers exigen
    // `require_permission("notificaciones", "manage")`. Un rol con solo `view`
    // —el que se le da a quien debe RECIBIR comunicados— entraba, escribía el
    // comunicado entero y se comía un 403 al pulsar Enviar.
    // Migrada a la capa React el 2026-09-15, en la misma pasada que
    // Comunicaciones. `page-notifications.js` se borró en ese cambio, así que
    // **el retroceso de una línea ya no existe para esta pantalla**.
    //
    // Admite sub-ruta: `#notificaciones/<pestaña>` abre una de las tres
    // (`nueva`, `historial`, `plantillas`). Por ahí entra la pestaña
    // «Plantillas» del Panel de administración, que antes incrustaba aquí el
    // gestor llamando a `PageNotifications.renderTemplates()`.
    notifications:   { react: 'notificaciones',              title: 'Notificaciones',     module: 'notificaciones', permiso: ['notificaciones', 'manage'] },
    // Migrada a la capa React el 2026-09-15. `page-communications.js` se borró
    // en ese mismo cambio, así que **el retroceso de una línea ya no existe
    // para esta pantalla**: no hay versión vanilla a la que volver. Sale del
    // historial de git si hiciera falta.
    communications:  { react: 'comunicaciones',              title: 'Comunicaciones',    module: 'notificaciones', permiso: ['notificaciones', 'manage'] },
    // Migrada a la capa React el 2026-09-25, las quince secciones a la vez:
    // `admin` es UNA entrada y el router solo admite `fn` o `react`, así que
    // media pantalla no se podía migrar sin partir la ruta. `page-admin.js` se
    // borró en ese mismo cambio —2.637 líneas, el archivo más grande que
    // quedaba—, así que **el retroceso de una línea ya no existe para esta
    // pantalla**. Sale del historial de git si hiciera falta.
    //
    // Admite sub-ruta: `#admin/<seccion>`. Sin ella abre el Panel.
    admin:           { react: 'administracion',              title: 'Administración',     module: 'administracion' },
    induction:       { react: 'induccion',                   title: 'Inducción',          module: null, soloGestion: true },
    reports:         { react: 'informes',                    title: 'Informes',           module: null, permiso: ['reportes', 'manage'] },
    analytics:       { react: 'analitica',                   title: 'Analítica',          module: null, permiso: ['analitica', 'manage'] },
    // Migrada a la capa React el 2026-09-03; `page-profile.js` se borró el
    // 2026-09-04, así que **el retroceso de una línea ya no existe para esta
    // pantalla**: no hay versión vanilla a la que volver. Si hiciera falta,
    // sale del historial de git (último commit con el archivo vivo).
    //
    // Admite sub-ruta: `#perfil/ficha` abre el panel de Mi ficha al montar. Es
    // por donde entra el botón «Completar» del aviso de datos pendientes del
    // shell, que antes llamaba a PageProfile.openMiFicha().
    profile:         { react: 'perfil',                     title: 'Mi Perfil',          module: null },
    // Migrada el 2026-09-25 junto con Administración, y con el MISMO componente:
    // esta pantalla y la sección `#admin/audit` eran dos tablas del mismo
    // endpoint, cada una con la mitad de las columnas —una tenía el filtro y la
    // otra el motivo— y ninguna completa. `page-audit.js` se borró.
    audit:           { react: 'auditoria',                   title: 'Auditoría',          module: 'administracion' },
    // Primera pantalla de la capa React. `react` en vez de `fn`: el router la
    // monta con window.PortalReact en lugar de llamar a un módulo vanilla.
    // Quitar esta línea devuelve el portal a como estaba — ese es el precio del
    // retroceso, y por eso la convivencia se montó así.
    sistema:         { react: 'sistema',                     title: 'Sistema de diseño',  module: null, soloGestion: true },
  };

  let _currentPage = null;
  // `_avatarUrl` se fue con el armazón. Se resolvía aquí con un `/auth/me` suelto
  // porque `Auth.saveSession` no guarda la foto; ahora es una consulta del propio
  // menú, que además la vuelve a pedir al recuperar el foco —la URL es prefirmada
  // y caduca, y antes se quedaba rota hasta la siguiente recarga—.
  // Protección contra pérdida de información (revisión 2026-07 · Fase 2.6):
  // último hash "confirmado" (una navegación ya completada) — si el usuario
  // cancela un cambio de sección con un formulario sin guardar, se revierte
  // el hash a este valor en vez de dejar la URL desincronizada del contenido.
  let _confirmedHash = location.hash || '#dashboard';

  // Pregunta antes de descartar un formulario sin guardar (marcado por
  // Utils.modal en utils.js). Si el usuario decide quedarse, revierte el
  // hash (que ya pudo haber cambiado por un <a href="#...">) a la última
  // sección confirmada. Devuelve true si la navegación puede continuar.
  async function _confirmNavigateAway() {
    // La pregunta vive en `Utils` desde el 2026-09-10 y no aquí: este archivo y
    // `utils.js` tenían el mismo texto copiado, y dos copias de una pregunta
    // son dos preguntas que se pueden contestar distinto sin que nadie lo note.
    if (!(await Utils.confirmarDescartarCambios())) {
      if (location.hash !== _confirmedHash) history.replaceState(null, '', _confirmedHash);
      return false;
    }
    window._appFormDirty = false;
    Utils.closeModal();
    return true;
  }

  // ── Inicialización ───────────────────────────────────────────────
  async function init() {
    document.body.classList.remove('app-loading');

    // Sin almacenamiento no hay portal: la sesión vive en `localStorage` y
    // `api.js` lee el token de ahí en CADA petición. Cuando el navegador lo
    // tiene bloqueado —una directiva de empresa, que en una intranet no es
    // hipótesis— lo que ocurría era un `SecurityError` sin atrapar en el primer
    // `Auth.isLoggedIn()`: pantalla en blanco y un rastro en la consola que no
    // dice nada a quien lo sufre. Comprobarlo aquí lo convierte en una frase.
    if (!Utils.hayAlmacenamiento()) {
      document.body.innerHTML = `
        <div class="alert alert-error" style="margin:3rem auto;max-width:34rem;line-height:1.6">
          <strong>Este navegador tiene bloqueado el almacenamiento del sitio.</strong><br><br>
          El portal necesita guardar la sesión en el navegador para funcionar.
          Suele pasar con la navegación privada o con una directiva de la
          empresa que bloquea los datos de sitio.<br><br>
          Prueba en una ventana normal; si sigue igual, avísale a sistemas.
        </div>`;
      return;
    }

    // Escuchar hash changes — translate Spanish hash alias
    window.addEventListener('hashchange', async () => {
      // El guard de "formulario sin guardar" vive dentro de navigate() (cubre
      // también las llamadas directas onclick="APP.navigate(...)", no solo
      // los enlaces de hash) — ver comentario ahí.
      if (window._appFormDirty && !(await _confirmNavigateAway())) return;
      if (_resetTokenFromHash()) { showReset(); return; }   // enlace de recuperación
      if (_formTokenFromHash()) { showPublicForm(); return; }  // formulario público
      const raw = location.hash.slice(1) || 'dashboard';
      navigate(raw);  // navigate() ya traduce vía HASH_ALIAS
    });

    // Cierre/recarga de pestaña con cambios sin guardar: advertencia nativa del navegador.
    window.addEventListener('beforeunload', e => {
      if (window._appFormDirty) { e.preventDefault(); e.returnValue = ''; }
    });

    // Protección contra pérdida de información en el formulario PÚBLICO de
    // Recolección de Datos (revisión 2026-07-24, punto 1): esta pantalla vive
    // fuera del sistema de modales (Utils.modal), así que el listener delegado
    // de utils.js no la cubre — se marca "sucio" aquí, y se reutilizan las
    // mismas protecciones ya existentes (guard de hashchange más arriba y
    // beforeunload) sin duplicar lógica de confirmación.
    //
    // Se delega en `#pubform-screen`, no en lo de dentro: desde el 2026-09-28 el
    // contenido lo pinta React, que reemplaza los hijos del contenedor. Un
    // listener colgado de un hijo moriría con el primer render; el contenedor
    // sobrevive y los eventos suben hasta él.
    document.getElementById('pubform-screen')?.addEventListener('input', () => {
      window._appFormDirty = true;
    });
    document.getElementById('pubform-screen')?.addEventListener('change', () => {
      window._appFormDirty = true;
    });

    // Los ocho listeners de las pantallas de acceso —el submit de cada
    // formulario, el ojo de la contraseña, «¿olvidaste…?», los dos «volver al
    // login» y «volver al inicio»— se fueron el 2026-09-28 con la pantalla.
    // Colgaban de elementos que ahora pinta React: habrían muerto en el primer
    // render, en silencio, y la pantalla de entrada habría dejado de responder
    // sin que nada fallara.

    // Aquí colgaban los QUINCE listeners del armazón: el cajón del menú y su
    // velo, cerrar sesión, contraer a riel, el acordeón, la búsqueda global con
    // su Ctrl/Cmd+K, la campana y su panel, el selector de apariencia, «¿Cómo
    // funciona?» y la barra inferior de móvil. Se fueron el 2026-09-28 con el
    // armazón: colgaban de elementos que ahora pinta React y habrían muerto en
    // el primer render, en silencio.
    //
    // Los dos atajos de teclado se fueron con el campo que enfocaban: `/` y
    // Ctrl/Cmd+K viven en `features/armazon/BusquedaGlobal.tsx`.

    // Clic en una mención (@compañero) → su FICHA del directorio, no la lista.
    //
    // Hasta el 2026-09-11 esto hacía `navigate('directory')` a secas y tiraba el
    // `data-uid`, que es justo el dato que hace falta: pulsabas `@María` y
    // acababas en la lista de trescientas personas, buscándola a mano. El enlace
    // no fallaba — llevaba a un sitio menos útil del que prometía.
    document.addEventListener('click', e => {
      const m = e.target.closest('.mention[data-uid]');
      if (!m || !Auth.hasModule('directorio')) return;
      const uid = m.dataset.uid;
      // El alias en ESPAÑOL, no la clave interna: es el que sale en la barra
      // de direcciones y el que alguien va a copiar y pegar. `navigate` lo
      // traduce por `HASH_ALIAS`, así que las dos formas llegan — pero solo
      // una deja una URL igual a la del enlace del menú.
      navigate(/^\d+$/.test(uid || '') ? `directorio/${uid}` : 'directorio');
    });

    // Registrar service worker (PWA instalable)
    if ('serviceWorker' in navigator) {
      // Se traga a propósito y no se registra: falla de forma esperada sin
      // HTTPS, en navegación privada y con el almacenamiento restringido. El
      // service worker solo aporta que el portal sea instalable; sin él todo
      // funciona igual, así que un aviso por consola en cada arranque sería
      // ruido permanente por algo que no es un problema.
      navigator.serviceWorker.register('/static/sw.js').catch(() => {});
    }

    // Cargar personalización del portal (nombre, logo, colores)
    await loadBranding();


    // Enlace de recuperación de contraseña (#reset?token=...) tiene prioridad
    if (_resetTokenFromHash()) {
      showReset();
    } else if (_formTokenFromHash()) {
      // Formulario público de Recolección de Datos (#formulario?token=...)
      showPublicForm();
    } else if (location.hash === '#tour') {
      // La portada (`/`) ofrece «Ver cómo funciona» junto a «Entrar». El
      // recorrido vive aquí, no allí: la portada no ejecuta lógica del
      // producto, solo enlaza a ella. No necesita sesión.
      showLogin();
      if (window.Onboarding) setTimeout(() => Onboarding.open(true), 150);
    } else if (Auth.isLoggedIn()) {
      await startApp();
    } else {
      // Sin sesión, con destino o sin él: el ACCESO.
      //
      // CON hash llegó por un enlace profundo —`/#cursos` de un correo, o una
      // de las seis puertas de la portada— que se reenvió aquí. Se le enseña
      // el acceso y no la portada por dos razones: el hash sobrevive al login
      // y luego `startApp()` navega ahí, y mandarlo a `/` lo devolvería aquí
      // en bucle.
      //
      // SIN hash, hasta el 2026-09-04 esto llamaba a `showLanding()`, que hace
      // `location.href = '/'`. La consecuencia, comprobada en el navegador: el
      // botón principal de la portada —«Entrar al portal», que apunta a
      // `/app`— DEVOLVÍA A LA PORTADA. La puerta más visible del producto no
      // llevaba a ninguna parte, y el rebote además hacía descargar el portal
      // entero para nada.
      //
      // Pedir `/app` es pedir el portal, y a quien lo pide sin sesión se le
      // enseña la puerta; no se le manda fuera. Vale igual para quien tiene la
      // aplicación instalada: el `start_url` del manifiesto es `/app`.
      //
      // `showLanding()` sigue existiendo y sigue usándose donde sí
      // corresponde: el enlace «volver» de la pantalla de acceso.
      showLogin();
    }
  }

  // ── Recuperación de contraseña por enlace de correo ──────────────
  function _resetTokenFromHash() {
    const h = location.hash || '';
    if (!h.startsWith('#reset')) return null;
    const qs = h.includes('?') ? h.split('?')[1] : '';
    return new URLSearchParams(qs).get('token');
  }

  function showReset() { _mostrarAcceso('acceso-restablecer'); }

  // `onResetSubmit` se fue el 2026-09-28 con la pantalla. Comprobaba el largo
  // de la contraseña con `if (p1.length < 8)` y esa era la ÚNICA comprobación
  // que existía: el servidor no miraba nada. Ahora la hace `exigir_clave_valida`
  // en `core/auth.py`, y la pantalla la repite solo para responder rápido.

  // ── Formulario público de Recolección de Datos (por token, sin login) ──────
  function _formTokenFromHash() {
    const h = location.hash || '';
    if (!h.startsWith('#formulario')) return null;
    const qs = h.includes('?') ? h.split('?')[1] : '';
    return new URLSearchParams(qs).get('token');
  }

  /**
   * El formulario público, que desde el 2026-09-28 es una pantalla de React.
   *
   * Aquí vivían 90 líneas que armaban el formulario con `innerHTML` a partir de
   * la definición que devuelve el servidor. Se fueron con su defecto: el texto
   * de ayuda de un campo (`f.help`) se interpolaba EN CRUDO mientras la
   * etiqueta, las opciones, los valores y la nota de devolución sí pasaban por
   * `Utils.escapeHtml` — y esta pantalla **no pide sesión**.
   *
   * `APP.submitPublicForm` ya no existe: el envío es del componente. Estaba
   * enganchado con `onclick="APP.submitPublicForm()"` dentro del HTML generado.
   */
  function showPublicForm() {
    ['login-screen', 'forgot-screen', 'reset-screen', 'app-shell'].forEach(id =>
      document.getElementById(id)?.classList.add('hidden'));
    document.getElementById('pubform-screen')?.classList.remove('hidden');
    // La pantalla recién cargada empieza "limpia": el guard de navegación mira
    // esta bandera, y sin ponerla a false arrastraría el estado de la anterior.
    window._appFormDirty = false;
    const contenedor = document.getElementById('pubform-screen');
    if (contenedor && window.PortalReact) {
      window.PortalReact.montar('formulario-publico', contenedor);
    }
  }

  /** La portada dejó de ser una capa de esta página: es un documento aparte
   *  servido en `/` (ver app/static/bienvenida.html). Así que «volver al
   *  inicio» es una navegación de verdad, no ocultar divs.
   *
   *  Se va SIN hash a propósito. Con hash, `/` lo reenviaría de vuelta aquí
   *  —es lo que mantiene vivos los enlaces de los correos— y quedaría un
   *  bucle. Al cerrar sesión tampoco interesa conservar el destino. */
  function showLanding() {
    location.href = '/';
  }

  // ── Branding / personalización ───────────────────────────────────
  let _branding = null;
  async function loadBranding() {
    try {
      _branding = await API.get('/auth/branding');
      window._branding = _branding;
      applyBranding(_branding);
    } catch (err) {
      // Seguir con la marca por defecto es lo correcto: el portal funciona sin
      // logo propio. Lo que no era correcto es callarlo — «el logo no sale» era
      // indistinguible de «nunca se configuró», y son dos problemas distintos
      // con dos culpables distintos.
      console.warn('[APP] No se pudo cargar la personalización del portal:', err);
    }
  }

  function applyBranding(b) {
    if (!b) return;
    document.title = `${b.portal_name} — ${b.company_name}`;
    // Las pantallas de ACCESO ya no se tocan desde aquí. Hasta el 2026-09-28
    // esta función metía la marca en su DOM con selectores POSICIONALES —el
    // primer y el último `div` de dentro de otro `div`—: reordenarlos movía el
    // nombre de la empresa al sitio del nombre del portal, sin dar error. Ahora
    // la marca es un DATO que la pantalla pide, y además React repinta, así que
    // cualquier cosa escrita en su DOM desde fuera se perdería igual.
    // El logo y el nombre ya no se escriben en el DOM desde aquí. Esto metía
    // `cssText` e `innerHTML` en `.logo-mark-sm` y ocultaba `.sidebar-brand`
    // buscándolos por clase; el menú es de React desde el 2026-09-28 y lo
    // escrito desde fuera se perdería en el primer render. La marca es un dato
    // (`window._branding`) y la pinta `MenuLateral`.

    // Colores (si difieren del default y el usuario no tiene tema custom propio)
    // Se compara contra la marca de fábrica que declara `theme.js`, no contra
    // una copia del hexadecimal: el color por defecto se define en un solo
    // sitio y aquí solo se pregunta si el servidor manda otro distinto.
    if (b.primary_color && b.primary_color !== Theme.porDefecto.primary) {
      document.documentElement.style.setProperty('--brand', b.primary_color);
    }
  }

  /**
   * El armazón: menú, cabecera, búsqueda, campana, aviso de ficha y barra
   * inferior. Una raíz de React que se monta al entrar y no se desmonta al
   * navegar (ver `features/armazon/Cromo.tsx`).
   *
   * Se monta AQUÍ, justo después de destapar el shell y antes de los dos
   * candados —Habeas Data y datos iniciales— porque es donde el armazón viejo
   * llamaba a `setupSidebar()`: los dos diálogos se abren sobre el portal ya
   * pintado, no sobre una pantalla en blanco.
   */
  function _montarCromo() {
    const cont = document.getElementById('app-chrome');
    if (cont && window.PortalReact) window.PortalReact.montarCromo(cont);
  }

  async function startApp() {
    await Auth.loadModules();
    document.getElementById('login-screen').classList.add('hidden');
    document.getElementById('forgot-screen').classList.add('hidden');
    document.getElementById('app-shell').classList.remove('hidden');
    _montarCromo();

    // Comprobar cambio de contraseña obligatorio
    const user = Auth.getUser();
    if (user?.must_change_password) {
      Utils.toast('Debes cambiar tu contraseña antes de continuar', 'warning');
      navigate('profile');
      // Abrir automáticamente el modal de cambio (forzado) para que no se pierda
      setTimeout(() => {
        if (typeof ClavePrimerIngreso !== 'undefined') ClavePrimerIngreso.abrir();
      }, 600);
      return;
    }

    // Habeas Data PRIMERO: la autorización de tratamiento tiene que preceder a la
    // recolección (Ley 1581/2012), y el paso siguiente le pide al colaborador su
    // fecha de nacimiento, teléfono, ciudad y lo que Bienestar haya marcado como
    // suyo. Hasta la revisión 2026-08-26 el orden era el inverso, justificado con
    // que "sin completar los datos la API responde 403 a todo lo demás" — y era
    // falso: /legal/pending y /legal/accept usan get_current_user directo, y
    // /legal/public/{key} no pide autenticación, así que los tres están exentos del
    // candado igual que los de onboarding. Con el orden viejo, además, quien
    // rechazara los documentos obligatorios ya había entregado sus datos y ya había
    // quedado ACTIVADO (cursos asignados y evento de bienvenida emitido).
    if (window.Legal) {
      const ok = await Legal.gate();
      if (!ok) return;   // el usuario cerró sesión sin aceptar
    }

    // Datos obligatorios del alta rápida (revisión 2026-07-30).
    if (window.DatosIniciales) {
      const ok = await DatosIniciales.gate();
      if (!ok) return;   // cerró sesión sin completar
    }

    navigate(_destinoDeEntrada());

    // Mostrar landing/onboarding a usuarios nuevos (si admin lo habilitó)
    if (window.Onboarding && Onboarding.shouldShow()) {
      setTimeout(() => Onboarding.open(), 800);
    }
  }

  // ── Auth forms ───────────────────────────────────────────────────
  //
  // `onLoginSubmit` y `onForgotSubmit` vivían aquí y se fueron el 2026-09-28
  // con la pantalla. Leían los campos con `getElementById` y manejaban a mano el
  // botón, su spinner y su texto —`btnText.textContent = 'Ingresando...'`—; eso
  // ahora es estado del componente.
  //
  // `Auth.login` NO se movió, y es deliberado: es quien guarda el token y el
  // usuario. La pantalla lo llama; dos versiones de la sesión se desincronizan.

  /**
   * Las tres pantallas de acceso, en React desde el 2026-09-28.
   *
   * `app.js` sigue decidiendo CUÁL se ve —es quien sabe si hay sesión, si el
   * hash trae un token de recuperación o si el servidor acaba de dar un 401— y
   * React pinta la que toque. Los tres modos se montan en `#login-screen`.
   *
   * `showLogin` es contrato: lo llaman `auth.js` al cerrar sesión y `api.js`
   * ante un 401, y ninguno de los dos sabe nada de React.
   */
  function _mostrarAcceso(clave) {
    ['forgot-screen', 'reset-screen', 'pubform-screen', 'app-shell'].forEach(id =>
      document.getElementById(id)?.classList.add('hidden'));
    const cont = document.getElementById('login-screen');
    if (!cont) return;
    cont.classList.remove('hidden');
    if (window.PortalReact) {
      // El armazón se DESMONTA, no se esconde. Ocultar `#app-shell` deja su
      // raíz viva detrás, y la campana pregunta cada minuto: ante un 401
      // `api.js` llama a `showLogin()`, que es esta función. Con el armazón
      // vivo eso sería un 401 y un `showLogin()` por minuto para siempre,
      // encima de la pantalla de entrada y sin que se vea nada raro.
      window.PortalReact.desmontarCromo();
      window.PortalReact.montar(clave, cont);
    }
  }

  function showLogin()  { _mostrarAcceso('acceso'); }
  function showForgot() { _mostrarAcceso('acceso-olvide'); }

  /** El destino con el que arranca la sesión, filtrado por lo que el rol alcanza.
   *
   *  Existe por la PORTADA. Desde el 2026-09-04 ofrece seis puertas públicas
   *  —`/app#tramites`, `/app#formacion`, `/app#kudos`…— y el hash sobrevive al
   *  inicio de sesión, así que quien pulsa «Formación» aterriza en Formación
   *  después de escribir su contraseña. Ese es el mejor momento de la portada.
   *
   *  Pero la portada NO PUEDE saber qué módulos alcanza el rol de quien pulsa:
   *  `/api/auth/my-modules` exige sesión, y los módulos se resuelven por rol y
   *  por usuario (`get_user_modules`), no por instalación. No hay forma pública
   *  de averiguarlo, y tampoco debería haberla.
   *
   *  Sin este filtro, alguien cuyo rol no tenga Celebraciones vería como PRIMERA
   *  cosa tras escribir su contraseña el toast rojo del guard de `navigate()`:
   *  «No tienes acceso a esta sección». Y no es culpa suya — venía de una puerta
   *  pública que el propio producto le enseñó.
   *
   *  Así que un destino inalcanzable EN LA ENTRADA aterriza en Inicio y en
   *  silencio. Dentro de la aplicación el aviso se queda tal cual: ahí sí es
   *  información útil, porque el enlace salió de un menú que ya conoce al
   *  usuario.
   */
  function _destinoDeEntrada() {
    const destino = location.hash.slice(1) || 'dashboard';
    // Mismo troceado que navigate(), para que «course-detail/5» resuelva igual.
    const [rawName] = destino.split('/');
    const config = PAGE_MAP[HASH_ALIAS[rawName] || rawName];
    if (!config) return 'dashboard';
    if (config.module && !Auth.hasModule(config.module)) return 'dashboard';
    return destino;
  }

  // ── Router ───────────────────────────────────────────────────────
  // `async` desde el 2026-09-10, cuando la pregunta de descarte dejó de ser el
  // `confirm()` del navegador —que era síncrono— y pasó a ser un diálogo del
  // portal. Las decenas de `onclick="APP.navigate(...)"` de las páginas no usan
  // el valor de retorno, así que no cambia nada para ellas.
  //
  // Y el camino normal sigue siendo síncrono: el `await` de abajo solo se
  // EVALÚA cuando hay un formulario sucio. Con `_appFormDirty` en falso el
  // cortocircuito lo salta y el cuerpo entero corre en el mismo tick, igual que
  // antes. Eso importa porque varias páginas hacen `navigate(x)` y siguen
  // tocando el DOM en la línea siguiente.
  async function navigate(page, params = {}) {
    // Protección contra pérdida de información (revisión 2026-07 · Fase 2.6):
    // cubre tanto los enlaces de hash (vía el listener de hashchange, que ya
    // resolvió esto antes de llegar aquí) como las llamadas directas
    // onclick="APP.navigate(...)" usadas en botones "Volver"/"Cancelar" de
    // muchas páginas — por eso el guard vive aquí y no solo en el listener.
    if (window._appFormDirty && !(await _confirmNavigateAway())) return;
    // Soporta parámetros: "course-detail/5"
    const [rawName, ...rest] = (page || 'dashboard').split('/');
    // Traducir hash español → clave interna del PAGE_MAP
    const pageName = HASH_ALIAS[rawName] || rawName;
    window._routeParams = rest;

    const config = PAGE_MAP[pageName];
    if (!config) { navigate('dashboard'); return; }

    // Guard de módulo
    if (config.module && !Auth.hasModule(config.module)) {
      Utils.toast('No tienes acceso a esta sección', 'error');
      navigate('dashboard');
      return;
    }
    // Guard de permiso (Informes, Analítica, Documentos, Recursos y Datos).
    //
    // Antes esto comparaba el NOMBRE del rol contra ['coordinador','admin'], y
    // el menú de al lado ya usaba is_staff: un rol personalizado marcado como
    // administrador veía la opción y al pulsarla lo devolvía a Inicio diciendo
    // que no tenía acceso, aunque el servidor sí lo autorizaba.
    //
    // Ahora se pregunta por el permiso, que es lo que autoriza el backend
    // (require_permission). Se conserva el nombre del rol como respaldo para
    // los casos en que la sesión no traiga permisos resueltos: así nadie pierde
    // acceso respecto de antes; solo lo ganan los roles a medida.
    const _esGestionClasica = () =>
      ['coordinador', 'admin'].includes(Auth.getUser()?.role);
    if (config.permiso &&
        !Auth.can(config.permiso[0], config.permiso[1]) && !_esGestionClasica()) {
      Utils.toast('No tienes acceso a esta sección', 'error');
      navigate('dashboard');
      return;
    }
    // Guard de gestión (Inducción): equivale a require_role('coordinador','admin')
    // del servidor, que acepta cualquier rol is_staff o is_admin.
    if (config.soloGestion && !Auth.getUser()?.is_staff && !_esGestionClasica()) {
      Utils.toast('No tienes acceso a esta sección', 'error');
      navigate('dashboard');
      return;
    }

    _currentPage = pageName;

    // Actualizar hash solo si es distinto (evita loop hashchange)
    const newHash = '#' + (page || pageName);
    if (location.hash !== newHash) history.replaceState(null, '', newHash);
    _confirmedHash = newHash;

    // El armazón se entera por un evento, y los dos globales son su estado
    // inicial: la raíz se monta en `startApp()` justo antes del primer
    // `navigate()`, y `render()` no es síncrono — si el evento llegara antes de
    // que React colgara su listener, la cabecera arrancaría sin título. Con los
    // globales da igual quién llegue primero.
    //
    // Aquí había 20 líneas que marcaban el ítem activo del menú a base de
    // `querySelectorAll` + `classList`, abrían su grupo del acordeón, escribían
    // el título y encendían la pestaña de la barra inferior. Todo eso lo decide
    // ahora el componente con el dato que le llega.
    const hash = page || pageName;
    window._paginaActual = pageName;
    window._tituloActual = config.title;
    window.dispatchEvent(new CustomEvent('portal:navego', {
      detail: { pagina: pageName, hash, titulo: config.title },
    }));

    // Barra de carga superior
    Utils.loaderStart();

    // Renderizar con transición de entrada
    const container = document.getElementById('page-container');
    // Desmontar la capa React ANTES de vaciar el contenedor. Si se vacía
    // primero, React se queda apuntando a nodos que ya no están en el
    // documento y el siguiente montaje avisa por consola.
    if (window.PortalReact) window.PortalReact.desmontar();
    container.classList.remove('page-enter');
    container.innerHTML = '<div class="page-loading"><div class="spinner-lg"></div></div>';
    try {
      if (config.react) {
        if (!window.PortalReact) {
          // Mismo criterio que el error de "falta un archivo" de abajo: decir
          // qué falta ahorra el rato de buscar el fallo donde no está.
          throw new Error('PortalReact is not defined');
        }
        container.innerHTML = '';
        const raiz = document.createElement('div');
        raiz.className = 'react-root';
        container.appendChild(raiz);
        window.PortalReact.montar(config.react, raiz);
      } else {
        config.fn();
      }
      void container.offsetWidth;       // reflow para reiniciar animación
      container.classList.add('page-enter');
    } catch (err) {
      // "PageAlgo is not defined" significa que el archivo de esa página no se
      // cargó: no es un fallo de la sección, es que al navegador le falta un
      // script. Pasa cuando queda una copia vieja en caché o cuando el
      // despliegue no reconstruyó los estáticos. Decirlo así ahorra el rato de
      // buscar el error donde no está.
      const falta = /(\w+) is not defined/.exec(err.message || '');
      if (falta) {
        const archivo = falta[1]
          .replace(/^Page/, 'page-')
          .replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase() + '.js';
        console.error(`[APP] No se cargó ${archivo} — ${err.message}`);
        container.innerHTML = `
          <div class="alert alert-error" style="line-height:1.6">
            <strong>Esta sección no se pudo cargar.</strong><br>
            Al navegador le falta un archivo del portal (<code>${archivo}</code>).<br><br>
            Prueba recargando con <strong>Ctrl + F5</strong>. Si sigue igual,
            avísale a sistemas: el despliegue quedó incompleto.
          </div>`;
      } else {
        console.error('[APP] Error al renderizar la página:', err);
        // `err` aquí es una excepción de JavaScript, no una respuesta HTTP, así
        // que no trae `status` y errorState la trata como fallo de servidor:
        // ofrece reintentar, que es lo correcto —volver a montar la página suele
        // funcionar— y deja el mensaje técnico abajo para reportarlo.
        container.innerHTML = Utils.errorState(err, {
          que: 'esta pantalla', reintentar: 'APP.navigate(location.hash.slice(1) || "dashboard")' });
      }
    }
    Utils.loaderDone();
  }

  /* ════════════════════════════════════════════════════════════════════════
   * Aquí vivía el ARMAZÓN: 530 líneas que se fueron el 2026-09-28.
   *
   * `setupSidebar`, `injectPinButtons`, `toggleFavorite`, `trackRecent`,
   * `openSidebar`, `closeSidebar`, `applySidebarMini`, `toggleSidebarMini`,
   * `_updateToggleIcon`, `setupNavAccordion`, `expandActiveGroup`,
   * `setupGlobalSearch`, `debounceFn`, `loadNotifBadge`,
   * `requestNotifPermission`, `toggleNotifPanel`, `renderNotifPanel`,
   * `avatarInitialsFallback`, `avisoFichaPendiente`, `descartarAvisoFicha` y
   * `_olvidarDescarte`. Con ellas, 269 líneas de marcado de `index.html`.
   *
   * Está todo en `app/static/src/features/armazon/`. El retroceso es un
   * `git revert`, no una línea: igual que con las pantallas ya migradas.
   *
   * ## Lo que este archivo conserva, y por qué
   *
   * El **router**. Es quien sabe traducir un hash español a una clave del
   * `PAGE_MAP`, quién puede entrar en cada sección y qué hacer con un formulario
   * a medio llenar. Eso no era armazón: es el reparto, y se migra en la capa C.
   *
   * ## Lo que se perdía en silencio, y cómo se evitó
   *
   * Siete cosas, ninguna de las cuales habría dado un error:
   *
   * 1. **Los favoritos.** `toggleFavorite` avisaba a Inicio con `portal:accesos`.
   *    El componente nuevo emitía `favoritos-cambiaron` — se guardaba bien y la
   *    tarjeta no se enteraba. Ahora el nombre se **importa** de
   *    `lib/eventos.ts`.
   * 2. **«Recientes».** `trackRecent` sacaba la etiqueta y el icono del DOM del
   *    menú y empezaba con `if (!el) return`. Con el menú en React ese selector
   *    no encuentra nada: habría dejado de registrar para siempre. Ahora sale de
   *    `navegacion.ts`.
   * 3. **La foto de perfil.** `Auth.saveSession` no guarda `avatar_url` —por eso
   *    `startApp` la pedía aparte—, así que `Auth.getUser()?.avatar_url` es
   *    siempre `undefined`: todo el mundo habría visto sus iniciales.
   * 4. **El corte del menú.** Tailwind `lg:` son 1024 px y el del armazón son
   *    900. Entre 901 y 1023 el CSS reservaba los 260 px y React mantenía el
   *    menú fuera de pantalla: una franja vacía y ningún menú.
   * 5. **`body.sidebar-mini`.** Es la clase de la que cuelga el margen de
   *    `.main-content`. Sin ponerla, el menú se encogía y el contenido se
   *    quedaba donde estaba.
   * 6. **Ctrl/Cmd+K.** Enfocaba `#global-search-input` con `?.` — sin ese id en
   *    el documento, dejaba de hacer nada.
   * 7. **El aviso del navegador.** La notificación del sistema operativo vivía
   *    dentro de `loadNotifBadge`, mezclada con el contador de la campana.
   *
   * Las siete están escritas en la ficha del módulo y cada una tiene su prueba.
   * ════════════════════════════════════════════════════════════════════════ */

  // `showForgot` se exporta desde el 2026-09-28. Antes solo lo usaba un listener
  // de aquí dentro; ahora lo llama la pantalla de React al pulsar «¿olvidaste tu
  // contraseña?». Sin exportarlo, esa llamada —escrita con `?.()` para no
  // reventar durante el arranque— NO HACÍA NADA y no daba error: el enlace se
  // pulsaba y la pantalla se quedaba igual. Lo encontró la pasada del navegador.
  // `avatarInitialsFallback`, `avisoFichaPendiente` y `descartarAvisoFicha` se
  // fueron con el armazón. Los dos últimos los llamaba la pantalla de Perfil al
  // guardar la ficha, con `window.APP?.avisoFichaPendiente?.()`: ese `?.` habría
  // dejado el aviso en pantalla pidiendo datos ya entregados, sin error. Ahora
  // la pantalla emite `portal:ficha` y el armazón lo escucha.
  return { init, navigate, showLogin, showForgot, showLanding, startApp };
})();

// Arranque
document.addEventListener('DOMContentLoaded', () => APP.init());
